import emailjs from '@emailjs/browser'
import MuiAlert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useMemo, useRef, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import Select from 'react-select'
import countryList from 'react-select-country-list'
import s from './AgentForm.module.css'

const RECAPTCHA_SITE_KEY = '6LcWL8YrAAAAAG7Bh7yUYrmjeMQcRPZ6B7qaBYhS'
const EMAILJS_SERVICE_ID = 'service_23hhm2k'
const EMAILJS_TEMPLATE_ID = 'template_izf4uzt'
const EMAILJS_PUBLIC_KEY = 'WxFann324-3TLjm9L'

export const AgentForm = () => {
	const options = useMemo(() => countryList().getData(), [])
	const { t } = useTranslation()

	const [country, setCountry] = useState(null) 
	const [phone, setPhone] = useState('')
	const [recaptchaToken, setRecaptchaToken] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [successOpen, setSuccessOpen] = useState(false)
	const [errorOpen, setErrorOpen] = useState(false)
	const [alertMsg, setAlertMsg] = useState('')

	const form = useRef(null)
	const recaptchaRef = useRef(null)

	const handleClose = (_, reason) => {
		if (reason === 'clickaway') return
		setSuccessOpen(false)
		setErrorOpen(false)
	}

	const sendEmail = e => {
		e.preventDefault()

		const token = recaptchaRef.current?.getValue()
		if (!token) {
			setAlertMsg(t("captcha"))
			setErrorOpen(true)
			return
		}
		const hidden = form.current.querySelector(
			'input[name="g-recaptcha-response"]'
		)
		if (hidden) hidden.value = token

		setSubmitting(true)
		emailjs
			.sendForm(
				EMAILJS_SERVICE_ID,
				EMAILJS_TEMPLATE_ID,
				form.current,
				EMAILJS_PUBLIC_KEY
			)
			.then(() => {
				 setAlertMsg(t("success"))
				setSuccessOpen(true)

				e.target.reset()
				setPhone('')
				setCountry(null)
				setRecaptchaToken(null)
				recaptchaRef.current?.reset()
			})
			.catch(err => {
				try {
					console.error('EmailJS error:', err?.status, JSON.parse(err?.text))
				} catch {
					console.error('EmailJS error:', err)
				}
				 setAlertMsg(t("error"))
				setErrorOpen(true)
				setRecaptchaToken(null)
				recaptchaRef.current?.reset()
			})
			.finally(() => setSubmitting(false))
	}

	return (
		<div className={s.AgentForm}>
			<h3>{t('agent-form')}</h3>
			<div className='container'>
				<div className={s.Wrapper}>
					<form ref={form} onSubmit={sendEmail} className={s.Form}>
						<label htmlFor='companyTitle'>{t('company')}</label>
						<input
							placeholder='Company title'
							name='company'
							id='companyTitle'
							type='text'
						/>

						<label htmlFor='country'>{t('country')}</label>
						<Select
							options={options}
							value={country}
							onChange={setCountry}
							placeholder='Select country'
							inputId='country'
							className='country-select'
							formatOptionLabel={opt => (
								<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
									<ReactCountryFlag
										svg
										countryCode={opt.value}
										style={{ fontSize: '1.2rem' }}
									/>
									<span>{opt.label}</span>
								</div>
							)}
						/>
						<input type='hidden' name='country' value={country?.label || ''} />

						<label htmlFor='city'>{t('city')}</label>
						<input placeholder='City' name='city' id='city' type='text' />

						<label htmlFor='phone'>{t('phone')}</label>
						<div className={s.Phone}>
							<PhoneInput
								defaultCountry='tr'
								value={phone}
								onChange={setPhone}
								international
								countrySelectorScrollable
								className={s.fullWidthPhone}
							/>
						</div>
						<input type='hidden' name='phone' value={phone} />

						<label htmlFor='email'>{t('email')}</label>
						<input placeholder='Email' name='email' id='email' type='email' />

						<label htmlFor='website'>{t('website')}</label>
						<input
							placeholder='Website'
							name='website'
							id='website'
							type='text'
						/>

						<label htmlFor='licenseNum'>{t('license')}</label>
						<input
							placeholder='License no.'
							name='licenseNum'
							id='licenseNum'
							type='text'
						/>

						<label htmlFor='providedWorkers'>{t('type-workers')}</label>
						<input
							placeholder='Type of workers'
							name='providedWorkers'
							id='providedWorkers'
							type='text'
						/>

						<textarea
							id='textComment'
							placeholder='Comment'
							name='textComment'
						/>

						<input
							type='hidden'
							name='g-recaptcha-response'
							value={recaptchaToken || ''}
						/>

						<ReCAPTCHA
							ref={recaptchaRef}
							sitekey={RECAPTCHA_SITE_KEY}
							onChange={t => setRecaptchaToken(t)}
							onExpired={() => setRecaptchaToken(null)}
						/>

						<input
							type='submit'
							value={submitting ? 'Sending...' : 'Send'}
							disabled={submitting}
						/>
					</form>
				</div>

				<Snackbar
					open={successOpen}
					autoHideDuration={3000}
					onClose={handleClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				>
					<MuiAlert
						onClose={handleClose}
						severity='success'
						variant='filled'
						elevation={6}
					>
						{alertMsg || 'Успешно!'}
					</MuiAlert>
				</Snackbar>

				<Snackbar
					open={errorOpen}
					autoHideDuration={4000}
					onClose={handleClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				>
					<MuiAlert
						onClose={handleClose}
						severity='error'
						variant='filled'
						elevation={6}
					>
						{alertMsg || 'Ошибка отправки'}
					</MuiAlert>
				</Snackbar>
			</div>
		</div>
	)
}
